import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LANGUAGE_EN, QUERY_AND_SESSION_KEYS, REFUND_OPTIONS, RETURN_METHOD_RETURN, RETURN_METHOD_SERVICE, ROUTES } from "~/constants";
import { fetchLogin, fetchSubmitProductReturns } from "~/services";
import {
    findFirstAvailableDateIndex,
    formatToRegularDate,
    getProductsData,
    getRefundOptions,
    isAnyComplaintSelected,
    isAnyReturnSelected,
    isAnySelectedProductFromResaleFlow,
    isLoginPage,
    isOnlyComplaintSelected,
    isWithdrawalAcknowledgmentOnly,
    isServiceFlowActive,
    selectableDatesData,
    shouldPreselectWithdrawalItems,
} from "~/cores/appCore";
import { format } from "date-fns";
import styles from "./DigitalReturnApp.module.scss";
import { HeaderComponent } from "~/components/headerComponent/HeaderComponent";
import ShopStylingWrapper from "../ShopStylingWrapper";
import {
    FLOW_STEP,
    getFlowSteps,
    getFlowStepNumber,
    getPostLoginPathname,
    hasReturnableProducts,
    getSelectedShipmentSolution,
    isResaleActive,
    onlyOneSimpleShipmentSolutionAvailable,
} from "~/cores/shipment.core";
import { preselectReturnableProducts } from "~/cores/selectedProductValuesCore";
import { isNumber } from "lodash";
import { useResizeDetector } from "react-resize-detector";
import { getErrorText } from "~/components/alerts/Error";
import classnames from "classnames";
import { StepperComponent } from "~/components/stepper/StepperComponent";
import SkeletonLoaderCard from "~/components/skeletons/SkeletonLoaderCard";
import ProductsFlexWrapper, { ProductFlexItemWrapper } from "~/components/productItem/ProductsFlexWrapper";
import { SentryUserManager } from "../appUtils/SentryComponents";
import translate from "translate";
import { showAllOrdersBrand } from "~/cores/brandDataCore";
import { serviceHasError, serviceIsSuccessfullyFetched, useServiceHook } from "rylak-common";
import { useQueryState } from "rylak-common/src/hooks/useQueryState.jsx";
import ItemsPageHeader from "./ItemsPageHeader";
import IngridFooter from "../IngridFooter";

export default function DigitalReturnApp(props) {
    const { sharedAppProps, router, navigate, system, initServiceData, setInitServiceData, brandData, brandName, isDemo, appOverrides, Page, language, setLanguage, addAlert } = props;

    const [productsExchangeServiceData, setProductsExchangeServiceData] = useState(null);
    const [similarProductsServiceData, setSimilarProductsServiceData] = useState(null);
    const [selectedProductsValues, setSelectedProductsValues] = useState(null);
    const [selectedRefundOption, setSelectedRefundOption] = useState(null);
    const [selectedRefundOptionResale, setSelectedRefundOptionResale] = useState(null);
    const [selectedPickupDateIndex, setSelectedPickupDateIndex] = useState(null);
    const [selectedShipmentSolutionData, setSelectedShipmentSolutionData] = useState(null);
    const [confirmationEmail, setConfirmationEmail] = useState("");
    const [email, setEmail, , emailInitialized] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.EMAIL,
        defaultValue: "",
        shouldUpdateQuery: false,
    });
    const [orderId, setOrderId, , orderIdInitialized] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.ORDER,
        defaultValue: "",
        shouldUpdateQuery: false,
    });
    const [withdrawal] = useQueryState({
        key: QUERY_AND_SESSION_KEYS.WITHDRAWAL,
        shouldUpdateQuery: false,
    });

    const [autoLogin, setAutoLogin] = useState(router.query[QUERY_AND_SESSION_KEYS.EMAIL] && router.query[QUERY_AND_SESSION_KEYS.ORDER] && !router.query[QUERY_AND_SESSION_KEYS.ACCEPT_POLICY]);

    const isOnLoginPage = isLoginPage(props);

    const resetData = useCallback(() => {
        setProductsExchangeServiceData({});
        setSimilarProductsServiceData({});
        setSelectedProductsValues([]);
        setSelectedRefundOption({});
        setSelectedRefundOptionResale({});
        setSelectedPickupDateIndex(findFirstAvailableDateIndex());
        setSelectedShipmentSolutionData(null);
        resetLoginService();
    }, []);

    const tryLogin = useCallback(() => {
        fetchLoginService({
            brandName,
            email,
            orderId,
            ...(withdrawal === true && { withdrawal: true }),
        });
    }, [brandName, email, orderId, withdrawal]);

    useEffect(() => {
        if (isOnLoginPage) {
            resetData();
        }
    }, [resetData, router.pathname]);

    const {
        serviceData: loginServiceData,
        fetchServiceData: fetchLoginService,
        clearServiceData: resetLoginService,
    } = useServiceHook({
        system,
        fetchRequested: false,
        fetchFn: fetchLogin,
        fetchData: { brandName, email, orderId },
    });

    useEffect(() => {
        // a bit ugly but we just alter the translations in here for now.
        if (serviceIsSuccessfullyFetched(loginServiceData) && language !== LANGUAGE_EN) {
            loginServiceData?.data?.products.forEach((product) => {
                const returnReasons = product?.returnReasonConfig?.returnReasons;
                returnReasons?.forEach((returnReason) => {
                    const translations = returnReason.translations;
                    if (!translations[language] && translations[LANGUAGE_EN]) {
                        translate(translations[LANGUAGE_EN], language).then((translatedText) => {
                            translations[language] = translatedText;
                        });
                    }
                });
                product?.complaintReasons?.forEach((complaintReason) => {
                    const translations = complaintReason.translations;
                    if (!translations[language] && translations[LANGUAGE_EN]) {
                        translate(translations[LANGUAGE_EN], language).then((translatedText) => {
                            translations[language] = translatedText;
                        });
                    }
                });
            });
        }
    }, [loginServiceData, language]);

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(loginServiceData)) {
            appOverrides?.onLogin?.({ email });
            // A service flow skips item selection, so nothing downstream would notice that there
            // is nothing left to send in. Say so here rather than walking the customer through
            // the shipping step to be refused at Confirm.
            if (isServiceFlowActive({ loginServiceData }) && !hasReturnableProducts({ loginServiceData })) {
                addAlert({ content: getLocaleText("login.ERROR.NO_RETURNABLE_ITEMS") });
                return;
            }
            navigate(
                {
                    pathname: getPostLoginPathname({ loginServiceData }),
                },
                undefined,
                { shallow: true },
            );
        }
        if (serviceHasError(loginServiceData)) {
            addAlert({
                title: "",
                content: getErrorText({
                    getLocaleText,
                    localeKey: "login",
                    serviceData: loginServiceData,
                }),
            });
            setAutoLogin(false);
        }
    }, [loginServiceData]);

    useEffect(() => {
        if (autoLogin && emailInitialized && orderIdInitialized) {
            tryLogin();
        }
    }, [autoLogin, emailInitialized, orderIdInitialized, tryLogin]);

    useEffect(() => {
        // A service flow never refunds, so the refund option is not the customer's to choose - the
        // service effect below owns it. Without this guard that effect fights this one: the service
        // method matches neither isAnyReturnSelected nor isAnyComplaintSelected, so every time the
        // auto-selected items change this would clear the option again and submit refundType: null.
        if (isServiceFlowActive({ loginServiceData })) return;
        const refundOptions = loginServiceData?.data?.refundOptions;
        if (!refundOptions || refundOptions?.length === 0) {
            return;
        } else if (!(isAnyReturnSelected(appProps) || isAnyComplaintSelected(appProps))) {
            setSelectedRefundOption(null);
        } else if (refundOptions?.length === 1) {
            setSelectedRefundOption(getRefundOptions(appProps)[0].type);
        }
    }, [loginServiceData, selectedProductsValues]);

    useEffect(() => {
        if (withdrawal !== true) return;
        const products = getProductsData({ loginServiceData });
        if (!products?.length) return;
        // The withdrawal time limit has passed - don't pre-select anything. Otherwise every item would be
        // auto-added as a withdrawal and the customer could walk all the way to Confirm before the backend
        // rejects it. ReturnPage also blocks the Continue button in this case.
        if (loginServiceData?.data?.withdrawalTimeExceeded) return;
        // Acknowledgment-only withdrawals are always refunded as BASIC. An "offer shipping" withdrawal behaves
        // like a regular return, so its refund options follow the price setup and are not forced here.
        if (isWithdrawalAcknowledgmentOnly({ loginServiceData })) {
            setSelectedRefundOption(REFUND_OPTIONS.BASIC);
        }
        // Only pre-select items for the "pre-select all" and "whole order, locked" modes.
        // In the "individually selectable" mode the customer picks items themselves, like a regular return.
        if (!shouldPreselectWithdrawalItems({ loginServiceData })) return;
        // Only pre-select items that can still be withdrawn - skip already returned/withdrawn or otherwise
        // non-returnable items (the backend marks these with returnable === false).
        setSelectedProductsValues(preselectReturnableProducts(products, RETURN_METHOD_RETURN));
    }, [withdrawal, loginServiceData?.data]);

    useEffect(() => {
        if (!isServiceFlowActive({ loginServiceData })) return;
        const products = getProductsData({ loginServiceData });
        if (!products?.length) return;
        setSelectedProductsValues(preselectReturnableProducts(products, RETURN_METHOD_SERVICE));
        setSelectedRefundOption(REFUND_OPTIONS.BASIC);
    }, [loginServiceData?.data]);

    const pickupDateSelectionAllowed = loginServiceData.data?.pickupDateSelectionAllowed && !isAnyComplaintSelected({ selectedProductsValues });
    const returnTimeExceeded = loginServiceData?.data?.returnTimeExceeded;

    const sessionId = loginServiceData.data?.sessionId;
    const selectedShipmentSolution = getSelectedShipmentSolution({
        selectedProductsValues,
        loginServiceData,
        selectedShipmentSolutionData,
    });

    const {
        serviceData: submitReturnProductsServiceData,
        fetchServiceData: fetchSubmitReturnProducts,
        clearServiceData: resetSubmitReturnProducts,
    } = useServiceHook({
        system,
        fetchRequested: false,
        fetchFn: fetchSubmitProductReturns,
        onReceive: (responseData) => {
            // if (responseData?.foreignOrderId) {
            if (brandName === "cos") {
                // special treatment for HM
                navigate({
                    pathname: ROUTES.STATUS.pathname,
                    query: {
                        order: responseData?.foreignOrderId,
                        email: email,
                    },
                });
            } else {
                navigate({ pathname: ROUTES.SUCCESS_PAGE.pathname }, undefined, {
                    shallow: true,
                });
            }
        },
    });

    const triggerFetchSubmitReturnProducts = useCallback(() => {
        fetchSubmitReturnProducts({
            sessionId: sessionId,
            languageCode: language,
            refundType: selectedRefundOption,
            products: selectedProductsValues,
            pickupDate: isNumber(selectedPickupDateIndex) ? formatToRegularDate(selectableDatesData[selectedPickupDateIndex].date) : null,
            ...(withdrawal === true && { withdrawal: true, email, withdrawalConfirmEmail: confirmationEmail || email }),
            ...((selectedShipmentSolutionData || selectedShipmentSolution) && {
                shipmentCarrier: selectedShipmentSolution?.carrier,
                shipmentSolutionType: selectedShipmentSolution?.type,
                shipmentOption: selectedShipmentSolutionData?.foreignOptionId && {
                    foreignSessionId: selectedShipmentSolutionData?.foreignSessionId,
                    foreignOptionId: selectedShipmentSolutionData?.foreignOptionId,
                },
                pickupDate: isNumber(selectedShipmentSolutionData?.dateIndex) ? format(selectableDatesData[selectedShipmentSolutionData?.dateIndex].date, "yyyy-MM-dd") : null,
            }),
        });
    }, [sessionId, language, selectedRefundOption, selectedProductsValues, selectedPickupDateIndex, selectedShipmentSolutionData, selectedShipmentSolution, withdrawal, confirmationEmail, email]);

    const redirectToLoginPage =
        !loginServiceData.data &&
        router.pathname !== ROUTES.MAIN_BRAND_PATH.pathname &&
        router.pathname !== ROUTES.COMPLAINT_IMAGE.pathname &&
        !router.pathname.startsWith(ROUTES.EXCHANGE_PAYMENT_BASE.pathname);
    React.useEffect(() => {
        if (redirectToLoginPage) {
            navigate({ pathname: ROUTES.MAIN_BRAND_PATH.pathname }, undefined, {
                shallow: true,
            });
        }
    }, [redirectToLoginPage]);

    function onProductExchangeUpdated({ productExchangeServiceData, productId }) {
        productsExchangeServiceData[productId] = productExchangeServiceData;
        setProductsExchangeServiceData({ ...productsExchangeServiceData });
    }

    function onSimilarProductsUpdated({ similarProductServiceData, productId }) {
        similarProductsServiceData[productId] = similarProductServiceData;
        setSimilarProductsServiceData({ ...similarProductsServiceData });
    }

    const getLocaleText = system.getLocaleText;
    const { width: appContainerWidth, ref: appContainerRef } = useResizeDetector();
    const { width: appWidth, ref: appRef } = useResizeDetector();

    const appProps = {
        ...sharedAppProps,
        router,
        navigate,
        system,
        language,
        setLanguage,
        setOrderId,
        setEmail,
        orderId,
        email,
        tryLogin,
        currencyCode: loginServiceData?.data?.currencyCode,
        foreignOrderId: loginServiceData?.data?.foreignOrderId,
        getLocaleText,
        brandData,
        brandName,
        initServiceData,
        setInitServiceData,
        sessionId,
        loginServiceData,
        pickupDateSelectionAllowed,
        returnTimeExceeded,
        productsExchangeServiceData,
        similarProductsServiceData,
        onProductExchangeUpdated,
        onSimilarProductsUpdated,
        submitReturnProductsServiceData,
        triggerFetchSubmitReturnProducts,
        resetSubmitReturnProducts,
        selectedProductsValues,
        setSelectedProductsValues,
        selectedPickupDateIndex,
        setSelectedPickupDateIndex,
        selectedRefundOption,
        setSelectedRefundOption,
        selectedRefundOptionResale,
        setSelectedRefundOptionResale,
        selectedShipmentSolutionData,
        selectedShipmentSolution,
        setSelectedShipmentSolutionData,
        appOverrides,
        appContainerWidth,
        appWidth,
        isDemo,
        addAlert,
        withdrawal,
        confirmationEmail,
        setConfirmationEmail,
    };

    useEffect(() => {
        if (serviceIsSuccessfullyFetched(loginServiceData) && onlyOneSimpleShipmentSolutionAvailable(appProps)) {
            setSelectedShipmentSolutionData({ index: 0 });
        }
    }, [loginServiceData]);

    const flowSteps = getFlowSteps(appProps);
    const maxSteps = flowSteps.length;

    const pageHeadingsData = useMemo(() => {
        return {
            [ROUTES.MAIN_BRAND_PATH.pathname]: {
                // getHeading: () =>
                //     showAllOrdersBrand(props)
                //         ? getLocaleText("login.header.show-all-orders")
                //         : getLocaleText("login.header"),
                getBody: () =>
                    appOverrides?.loginDescriptionOverride ||
                    getLocaleText(withdrawal === true ? "login.header.body.withdrawal" : "login.header.body", null, {
                        plural: showAllOrdersBrand(props),
                    }),
                dataTestId: "page-header-login",
            },
            [ROUTES.RETURN_PAGE.pathname]: {
                // getHeading: () =>
                //     showAllOrdersBrand(props)
                //         ? null
                //         : isResaleActive(appProps)
                //         ? getLocaleText("return-items.header-resale")
                //         : getLocaleText("return-items.header"),
                getHeading: () => (showAllOrdersBrand(props) ? null : isResaleActive(appProps) ? getLocaleText("return-items.header_body-resale") : <ItemsPageHeader appProps={appProps} />),
                currentStep: getFlowStepNumber(appProps, FLOW_STEP.ITEMS),
                maxSteps,
                hideMobile: false,
                onBackClick: () =>
                    navigate({ pathname: ROUTES.MAIN_BRAND_PATH.pathname }, undefined, {
                        shallow: true,
                    }),
                dataTestId: "page-header-return",
            },
            [ROUTES.SHIPMENT_METHOD_PAGE.pathname]: {
                // getHeading: () => getLocaleText("pickup-date.header"),
                getBody: () =>
                    withdrawal === true
                        ? getLocaleText("pickup-date.header_body.withdrawal")
                        : isServiceFlowActive(appProps)
                          ? getLocaleText("pickup-date.header_body.service")
                          : isAnySelectedProductFromResaleFlow(appProps)
                            ? getLocaleText("pickup-date.header_body.with-resale")
                            : getLocaleText("pickup-date.header_body"),
                currentStep: getFlowStepNumber(appProps, FLOW_STEP.SHIPPING),
                maxSteps,
                hideMobile: false,
                onBackClick: () => router.back(),
                dataTestId: "page-header-shipment-method",
            },
            [ROUTES.CONFIRMATION_PAGE.pathname]: {
                // getHeading: () => getLocaleText("confirmation-page.header"),
                getBody: () => getLocaleText("confirmation-page.header_body"),
                currentStep: getFlowStepNumber(appProps, FLOW_STEP.CONFIRM),
                maxSteps,
                hideMobile: false,
                onBackClick: () => router.back(),
                dataTestId: "page-header-confirmation",
            },
            [ROUTES.SUCCESS_PAGE.pathname]: {
                getHeading: () =>
                    withdrawal === true
                        ? getLocaleText("success-page.header.withdrawal")
                        : isServiceFlowActive(appProps)
                          ? getLocaleText("success-page.header.service")
                          : isOnlyComplaintSelected(appProps)
                            ? getLocaleText("success-page.header-claim")
                            : getLocaleText("success-page.header"),
                dataTestId: "page-header-success",
            },
            [ROUTES.EXCHANGE_PAYMENT_COMPLETE.pathname]: {
                getHeading: () => getLocaleText("exchange-success-page.header"),
                dataTestId: "page-header-payment-complete",
            },
            [ROUTES.EXCHANGE_PAYMENT_FAILED.pathname]: {
                getHeading: () => getLocaleText("exchange-failed-page.header"),
                dataTestId: "page-header-payment-failed",
            },
        };
    }, [props, appProps, getLocaleText]);

    function getHeadingData() {
        return pageHeadingsData[router.pathname];
    }

    const headingData = getHeadingData();

    const currentStep = headingData?.currentStep;

    const pageRef = useRef();

    const onStepClick = useCallback(
        (step) => {
            if (currentStep === step) {
                return;
            }
            if (step === currentStep + 1) {
                // More forward
                pageRef?.current?.continue?.();
            }
            if (step < currentStep) {
                const foundHeadingKey = Object.keys(pageHeadingsData)?.find((key) => {
                    return pageHeadingsData[key]?.currentStep === step;
                });
                if (foundHeadingKey) {
                    navigate({ pathname: foundHeadingKey }, undefined, {
                        shallow: true,
                    });
                }
            }
        },
        [currentStep, pageRef, appProps],
    );

    return (
        <ShopStylingWrapper appProps={appProps} {...appProps} appContainerRef={appContainerRef} appRef={appRef} headingData={headingData} maxSteps={maxSteps}>
            <div className={classnames(styles["app-container"], appProps?.isInIframe && styles["app-container-iframe"])}>
                {autoLogin && isOnLoginPage ? (
                    <>
                        <ProductsFlexWrapper
                            productsComponents={[1, 2, 3].map((v) => {
                                return (
                                    <ProductFlexItemWrapper key={v}>
                                        <div
                                            style={{
                                                position: "relative",
                                                height: "150px",
                                            }}
                                        >
                                            <SkeletonLoaderCard />
                                        </div>
                                    </ProductFlexItemWrapper>
                                );
                            })}
                        ></ProductsFlexWrapper>
                        {/*<LoadingComponent className={styles["autologin-loading-spinner"]}*/}
                        {/*                  size={10}*/}
                        {/*                  getLocaleText={getLocaleText}/>*/}
                    </>
                ) : (
                    !redirectToLoginPage && (
                        <>
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                <div
                                    style={{
                                        maxWidth: "5000px",
                                        width: "100%",
                                    }}
                                >
                                    <StepperComponent getLocaleText={getLocaleText} currentStep={currentStep} steps={flowSteps} onClick={onStepClick} />
                                </div>
                            </div>
                            <HeaderComponent
                                {...headingData}
                                {...brandData}
                                heading={headingData?.getHeading && headingData.getHeading()}
                                body={headingData?.getBody && headingData.getBody()}
                                getLocaleText={getLocaleText}
                            />
                            <Page {...appProps} appProps={appProps} ref={pageRef} />
                        </>
                    )
                )}

                <IngridFooter appProps={appProps} />
            </div>

            <SentryUserManager email={email} orderId={orderId} />
        </ShopStylingWrapper>
    );
}
